// src/pages/USASalesPayoutPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { apiUrl } from "../config/api";

// ---------- Types ----------
type USAOrderItem = {
  id: number;
  bill_no: string;
  product_code: string | null;
  product_name: string | null;
  qty: number;
  unit_price: number;
  unit_cost: number;
  usa_net_usd: number;
  usa_net_thb: number;
  usa_qty_sold: number;
};

type USASaleRecord = {
  id: number;
  bill_no: string;
  order_item_id: number;
  qty: number;
  usd: number;
  thb: number;
  exchange_rate: number;
  note: string | null;
  sold_at: string | null;
  created_at: string | null;
};

type USAOrder = {
  bill_no: string;
  sale_channel: string | null;
  payment_method: string | null;
  usa_status: string;
  usa_payout_usd: number;
  usa_payout_thb: number;
  usa_exchange_rate: number;
  rec_date_be: string | null;
  rec_time: string | null;
  total_items: number;
  total_amount: number;
  discount_amount: number;
  created_at: string | null;
  order_items: USAOrderItem[];
  sale_records: USASaleRecord[];
};

type USAOrdersResponse = { ok?: boolean; items?: unknown; error?: string };

const TOKEN_STORAGE_KEY = "bg_shop_token";

const readonlyFieldStyle: React.CSSProperties = {
  backgroundColor: "#f8f9fa",
  cursor: "not-allowed",
};

// ---------- Helpers ----------
function toNumber(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function toUSAOrderItem(row: unknown): USAOrderItem | null {
  if (!row || typeof row !== "object") return null;
  const d = row as Record<string, unknown>;
  const id = Number(d.id);
  if (!Number.isInteger(id) || id <= 0) return null;
  return {
    id,
    bill_no: typeof d.bill_no === "string" ? d.bill_no.trim() : "",
    product_code: typeof d.product_code === "string" ? d.product_code : null,
    product_name: typeof d.product_name === "string" ? d.product_name : null,
    qty: toNumber(d.qty),
    unit_price: toNumber(d.unit_price),
    unit_cost: toNumber(d.unit_cost),
    usa_net_usd: toNumber(d.usa_net_usd),
    usa_net_thb: toNumber(d.usa_net_thb),
    usa_qty_sold: toNumber(d.usa_qty_sold),
  };
}

function toUSASaleRecord(row: unknown): USASaleRecord | null {
  if (!row || typeof row !== "object") return null;
  const d = row as Record<string, unknown>;
  const id = Number(d.id);
  if (!Number.isInteger(id) || id <= 0) return null;
  return {
    id,
    bill_no: typeof d.bill_no === "string" ? d.bill_no : "",
    order_item_id: Number(d.order_item_id),
    qty: toNumber(d.qty),
    usd: toNumber(d.usd),
    thb: toNumber(d.thb),
    exchange_rate: toNumber(d.exchange_rate),
    note: typeof d.note === "string" ? d.note : null,
    sold_at: typeof d.sold_at === "string" ? d.sold_at : null,
    created_at: typeof d.created_at === "string" ? d.created_at : null,
  };
}

function toUSAOrder(row: unknown): USAOrder | null {
  if (!row || typeof row !== "object") return null;
  const d = row as Record<string, unknown>;
  const bill_no = typeof d.bill_no === "string" ? d.bill_no.trim() : "";
  if (!bill_no) return null;
  return {
    bill_no,
    sale_channel: typeof d.sale_channel === "string" ? d.sale_channel : null,
    payment_method: typeof d.payment_method === "string" ? d.payment_method : null,
    usa_status:
      typeof d.usa_status === "string" && d.usa_status.trim()
        ? d.usa_status.trim().toUpperCase()
        : "PENDING",
    usa_payout_usd: toNumber(d.usa_payout_usd),
    usa_payout_thb: toNumber(d.usa_payout_thb),
    usa_exchange_rate: toNumber(d.usa_exchange_rate),
    rec_date_be: typeof d.rec_date_be === "string" ? d.rec_date_be : null,
    rec_time: typeof d.rec_time === "string" ? d.rec_time : null,
    total_items: toNumber(d.total_items),
    total_amount: toNumber(d.total_amount),
    discount_amount: toNumber(d.discount_amount),
    created_at: typeof d.created_at === "string" ? d.created_at : null,
    order_items: Array.isArray(d.order_items)
      ? d.order_items.map(toUSAOrderItem).filter((x): x is USAOrderItem => x != null)
      : [],
    sale_records: Array.isArray(d.sale_records)
      ? d.sale_records.map(toUSASaleRecord).filter((x): x is USASaleRecord => x != null)
      : [],
  };
}

function formatCurrency(value: number, currency: "THB" | "USD" = "THB"): string {
  const locale = currency === "USD" ? "en-US" : "th-TH";
  return value.toLocaleString(locale, { style: "currency", currency });
}

function formatDateTime(value: string | null): string {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("th-TH", {
    year: "2-digit", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

function getStatusLabel(status: string): string {
  return status === "COMPLETED" ? "เสร็จสิ้น" : "ยังไม่ครบ";
}

function getStatusBadgeClass(status: string): string {
  return status === "COMPLETED"
    ? "badge rounded-pill text-bg-success"
    : "badge rounded-pill text-bg-danger";
}

// ---------- Sale Record Modal ----------
type RecordModalState = {
  bill_no: string;
  order_item_id: number;
  product_code: string;
  product_name: string;
  remaining: number;
  edit?: USASaleRecord; // if editing
};

type RecordFormState = {
  qty: string;
  usd: string;
  thb: string;
  exchange_rate: string;
  note: string;
};

// ---------- Component ----------
export default function USASalesPayoutPage(): JSX.Element {
  const [orders, setOrders] = useState<USAOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [expandedBillNos, setExpandedBillNos] = useState<Record<string, boolean>>({});
  const [statusUpdatingBillNo, setStatusUpdatingBillNo] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ALL" | "PENDING" | "COMPLETED">("PENDING");
  // PMN-NOTE(/USASalesPayoutPage): per-bill in-detail item search — does NOT filter out non-matching items; only re-sorts matches to top.
  const [itemQueryByBill, setItemQueryByBill] = useState<Record<string, string>>({});
  // PMN-CRITICAL(/USASalesPayoutPage): per-bill exchange rate input (separate from server state until user saves). Saving sends PATCH /orders/:bill_no/usa-exchange-rate.
  const [billRateInput, setBillRateInput] = useState<Record<string, string>>({});
  const [savingBillRate, setSavingBillRate] = useState<string | null>(null);
  // PMN-CRITICAL(/USASalesPayoutPage): one-shot consolidate of legacy sale_channel='USA' bills → ONE USA_SHOP bill. Hidden after empty response.
  const [consolidating, setConsolidating] = useState(false);
  const [legacyHidden, setLegacyHidden] = useState(false);

  // Sale Record Modal
  const [recordModal, setRecordModal] = useState<RecordModalState | null>(null);
  const [recordForm, setRecordForm] = useState<RecordFormState>({
    qty: "1", usd: "", thb: "", exchange_rate: "", note: "",
  });
  const [savingRecord, setSavingRecord] = useState(false);

  // Undo
  const [undoingBillNo, setUndoingBillNo] = useState<string | null>(null);
  const [undoConfirmInput, setUndoConfirmInput] = useState<Record<string, string>>({});

  useEffect(() => {
    void loadOrders();
  }, []);

  async function loadOrders(): Promise<void> {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
      const resp = await fetch(apiUrl("/usa-shop-orders"), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = (await resp.json().catch(() => ({}))) as USAOrdersResponse;
      if (!resp.ok || !data.ok || !Array.isArray(data.items)) {
        throw new Error(data.error ?? "failed_to_fetch_usa_shop_orders");
      }
      const nextItems = data.items
        .map(toUSAOrder)
        .filter((x): x is USAOrder => x != null);
      setOrders(nextItems);
    } catch (err) {
      setError(err instanceof Error ? err.message : "failed_to_fetch_usa_shop_orders");
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter((o) => {
      if (statusFilter !== "ALL" && o.usa_status !== statusFilter) return false;
      if (!q) return true;
      const hay = [
        o.bill_no,
        o.payment_method ?? "",
        ...o.order_items.map((oi) => `${oi.product_code ?? ""} ${oi.product_name ?? ""}`),
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [orders, query, statusFilter]);

  // ---------- Sale Record Modal ----------
  function openCreateRecord(bill_no: string, item: USAOrderItem) {
    const remaining = item.qty - item.usa_qty_sold;
    if (remaining <= 0) return;
    // pre-fill exchange rate: last record's rate > bill-level rate > empty
    const bill = orders.find((b) => b.bill_no === bill_no);
    const lastRec = bill?.sale_records?.[bill.sale_records.length - 1];
    const fallbackRate = lastRec?.exchange_rate || bill?.usa_exchange_rate || 0;
    const rate = fallbackRate > 0 ? String(fallbackRate) : "";
    setRecordModal({
      bill_no,
      order_item_id: item.id,
      product_code: item.product_code ?? "",
      product_name: item.product_name ?? "",
      remaining,
    });
    setRecordForm({ qty: "1", usd: "", thb: "", exchange_rate: rate, note: "" });
  }

  function openEditRecord(bill_no: string, item: USAOrderItem, rec: USASaleRecord) {
    const remaining = item.qty - item.usa_qty_sold + rec.qty;
    setRecordModal({
      bill_no,
      order_item_id: item.id,
      product_code: item.product_code ?? "",
      product_name: item.product_name ?? "",
      remaining,
      edit: rec,
    });
    setRecordForm({
      qty: String(rec.qty),
      usd: String(rec.usd),
      thb: String(rec.thb),
      exchange_rate: String(rec.exchange_rate),
      note: rec.note ?? "",
    });
  }

  function closeRecordModal() {
    setRecordModal(null);
    setRecordForm({ qty: "1", usd: "", thb: "", exchange_rate: "", note: "" });
  }

  // PMN-CRITICAL(/USASalesPayoutPage): user types USD + rate → auto-suggest THB. User can override THB after.
  function onUsdOrRateChange(nextUsd: string, nextRate: string) {
    const usd = Number(nextUsd);
    const rate = Number(nextRate);
    if (Number.isFinite(usd) && Number.isFinite(rate) && usd > 0 && rate > 0) {
      const thb = Math.round(usd * rate * 100) / 100;
      setRecordForm((prev) => ({ ...prev, usd: nextUsd, exchange_rate: nextRate, thb: String(thb) }));
    } else {
      setRecordForm((prev) => ({ ...prev, usd: nextUsd, exchange_rate: nextRate }));
    }
  }

  async function saveRecord() {
    if (!recordModal) return;
    const qty = Number(recordForm.qty);
    const usd = Number(recordForm.usd || 0);
    const thb = Number(recordForm.thb || 0);
    const rate = Number(recordForm.exchange_rate || 0);

    if (!Number.isInteger(qty) || qty <= 0) { setError("จำนวนต้องเป็นจำนวนเต็มมากกว่า 0"); return; }
    if (qty > recordModal.remaining) { setError(`จำนวนเกินที่เหลือ (${recordModal.remaining})`); return; }
    if (!Number.isFinite(usd) || usd < 0) { setError("USD ต้องเป็นจำนวนไม่ติดลบ"); return; }
    if (!Number.isFinite(thb) || thb < 0) { setError("THB ต้องเป็นจำนวนไม่ติดลบ"); return; }
    if (!Number.isFinite(rate) || rate < 0) { setError("อัตราแลกต้องเป็นจำนวนไม่ติดลบ"); return; }
    // PMN-NOTE(/USASalesPayoutPage): no longer require rate or THB per record — backend falls back to bill-level usa_exchange_rate if both are 0.

    // Auto-fill THB from rate if user left THB at 0 but rate > 0
    const finalThb = thb > 0 ? thb : (rate > 0 && usd > 0 ? Math.round(usd * rate * 100) / 100 : 0);

    setSavingRecord(true);
    setError("");
    setSuccessMessage("");
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
      const body = {
        order_item_id: recordModal.order_item_id,
        qty,
        usd,
        thb: finalThb,
        exchange_rate: rate,
        note: recordForm.note.trim() || null,
      };
      const url = recordModal.edit
        ? apiUrl(`/usa-sale-records/${recordModal.edit.id}`)
        : apiUrl(`/orders/${encodeURIComponent(recordModal.bill_no)}/usa-sale-records`);
      const method = recordModal.edit ? "PATCH" : "POST";
      const resp = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(body),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.ok) {
        if (data?.error === "qty_exceeds_remaining") {
          throw new Error(`จำนวนเกินที่เหลือ (สูงสุด ${data?.meta?.remaining ?? data?.meta?.allowed})`);
        }
        throw new Error(typeof data?.error === "string" ? data.error : "failed_to_save_record");
      }
      setSuccessMessage(recordModal.edit ? "แก้ไขรายการขายเรียบร้อย" : "บันทึกรายการขายเรียบร้อย");
      closeRecordModal();
      await loadOrders();
    } catch (err) {
      setError(err instanceof Error ? err.message : "failed_to_save_record");
    } finally {
      setSavingRecord(false);
    }
  }

  async function deleteRecord(rec: USASaleRecord) {
    if (!window.confirm(`ลบรายการขาย #${rec.id} (qty ${rec.qty}, $${rec.usd}, ฿${rec.thb})?`)) return;
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
      const resp = await fetch(apiUrl(`/usa-sale-records/${rec.id}`), {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.ok) throw new Error(data?.error ?? "failed_to_delete");
      setSuccessMessage("ลบรายการเรียบร้อย");
      await loadOrders();
    } catch (err) {
      setError(err instanceof Error ? err.message : "failed_to_delete");
    }
  }

  async function handleConsolidateLegacy() {
    if (!window.confirm("รวมสินค้า USA จากบิลเก่า (sale_channel='USA') เป็น 1 บิลใหม่ (USA_SHOP)?\n\nการกระทำนี้จะ:\n- สร้างบิล USA_SHOP ใหม่ครอบของลอยทั้งหมด\n- ลบบิลเก่าทิ้ง\n- ทำให้ระบบเหลือเฉพาะ sale_channel='USA_SHOP'")) return;
    setConsolidating(true);
    setError("");
    setSuccessMessage("");
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
      const resp = await fetch(apiUrl("/usa-shop-orders/consolidate-legacy"), {
        method: "POST",
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.ok) throw new Error(data?.error ?? "failed_to_consolidate");
      const parts = [];
      if (data.consolidated_bills > 0) parts.push(`รวม ${data.consolidated_bills} บิลเก่า → บิลใหม่ ${data.bill_no} (${data.total_units} ชิ้น)`);
      if (data.renamed_residual > 0) parts.push(`เปลี่ยน sale_channel ของ ${data.renamed_residual} บิลเก่าที่ขายหมดแล้ว`);
      if (parts.length === 0) {
        setSuccessMessage("ไม่มี legacy USA bills เหลือ");
        setLegacyHidden(true);
      } else {
        setSuccessMessage(parts.join(" + "));
      }
      await loadOrders();
    } catch (err) {
      setError(err instanceof Error ? err.message : "failed_to_consolidate");
    } finally {
      setConsolidating(false);
    }
  }

  async function saveBillRate(bill_no: string) {
    const raw = billRateInput[bill_no];
    if (raw === undefined) return;
    const rate = Number(raw);
    if (!Number.isFinite(rate) || rate < 0) { setError("rate ต้องเป็นจำนวนไม่ติดลบ"); return; }
    setSavingBillRate(bill_no);
    setError("");
    setSuccessMessage("");
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
      const resp = await fetch(apiUrl(`/orders/${encodeURIComponent(bill_no)}/usa-exchange-rate`), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({ usa_exchange_rate: rate }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.ok) throw new Error(data?.error ?? "failed_to_save_rate");
      setOrders((prev) => prev.map((o) => o.bill_no === bill_no ? { ...o, usa_exchange_rate: rate } : o));
      setSuccessMessage(`บันทึก rate ของบิล ${bill_no} เรียบร้อย (1 USD = ${rate} THB)`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "failed_to_save_rate");
    } finally {
      setSavingBillRate(null);
    }
  }

  // ---------- Status + Undo ----------
  async function handleStatusUpdate(item: USAOrder, status: "PENDING" | "COMPLETED"): Promise<void> {
    setStatusUpdatingBillNo(item.bill_no);
    setError("");
    setSuccessMessage("");
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
      const resp = await fetch(apiUrl(`/orders/${encodeURIComponent(item.bill_no)}/usa-status`), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({ status }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.ok) throw new Error(data?.error ?? "failed_to_update_usa_status");
      setOrders((prev) =>
        prev.map((row) => (row.bill_no === item.bill_no ? { ...row, usa_status: status } : row))
      );
      setSuccessMessage(`อัปเดตสถานะบิล ${item.bill_no} เรียบร้อย`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "failed_to_update_usa_status");
    } finally {
      setStatusUpdatingBillNo(null);
    }
  }

  async function handleUndo(item: USAOrder): Promise<void> {
    setUndoingBillNo(item.bill_no);
    setError("");
    setSuccessMessage("");
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
      const resp = await fetch(apiUrl(`/orders/${encodeURIComponent(item.bill_no)}/undo-usa-transfer`), {
        method: "POST",
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.ok) {
        const err = data?.error ?? "failed_to_undo";
        if (err === "items_already_consumed") {
          throw new Error(`ของถูกใช้ต่อแล้ว (${(data?.meta?.consumed_codes ?? []).join(", ")})`);
        }
        if (err === "already_completed_cannot_undo") throw new Error("บิลเสร็จสิ้นแล้ว ห้าม undo");
        if (err === "has_sale_records_cannot_undo") {
          throw new Error(`บิลนี้มีประวัติการขาย ${data?.meta?.sale_records_count} รายการ ต้องลบประวัติก่อน undo`);
        }
        throw new Error(typeof err === "string" ? err : "failed_to_undo");
      }
      setOrders((prev) => prev.filter((row) => row.bill_no !== item.bill_no));
      setUndoConfirmInput((prev) => { const n = { ...prev }; delete n[item.bill_no]; return n; });
      setSuccessMessage(`ย้อนบิล ${item.bill_no} เรียบร้อย`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "failed_to_undo");
    } finally {
      setUndoingBillNo(null);
    }
  }

  return (
    <div className="min-vh-100 bg-light">
      <div className="container py-4 py-md-5">
        <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
          <h4 className="mb-0 text-danger">🇺🇸 บันทึกยอดขาย USA</h4>
          <a href="/" className="btn btn-link text-decoration-none">← กลับเมนู</a>
        </div>

        {/* PMN-NOTE(/USASalesPayoutPage): Legacy consolidate banner — one-shot migration for old sale_channel='USA' bills. Hidden after empty response. */}
        {!legacyHidden && (
          <div className="alert alert-info border-0 shadow-sm rounded-3 d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
            <div>
              <strong>📦 รวมสินค้า USA จากระบบเก่า</strong>
              <div className="small text-muted">
                ถ้ามี stock USA จากบิลเก่า (sale_channel='USA') ระบบจะรวมเป็นบิล USA_SHOP ใหม่ใบเดียว และ unify channel ให้
              </div>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              disabled={consolidating}
              onClick={() => void handleConsolidateLegacy()}
            >
              {consolidating ? "กำลังรวม…" : "🔗 รวมบิลเก่า"}
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="card border-0 shadow-sm rounded-4 mb-3">
          <div className="card-body p-3">
            <div className="row g-2 align-items-end">
              <div className="col-12 col-md-6">
                <label className="form-label small text-muted">ค้นหา</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="bill_no, รหัสสินค้า, ชื่อ"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label small text-muted">สถานะ</label>
                <div className="btn-group w-100" role="group">
                  {(["PENDING", "COMPLETED", "ALL"] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={`btn ${statusFilter === s ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setStatusFilter(s)}
                    >
                      {s === "ALL" ? "ทั้งหมด" : s === "PENDING" ? "ยังไม่ครบ" : "เสร็จสิ้น"}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && <div className="alert alert-warning small">{error}</div>}
        {successMessage && <div className="alert alert-success small">{successMessage}</div>}

        {loading ? (
          <div className="text-center text-muted py-5">กำลังโหลด…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-muted py-5">ไม่พบบิล USA</div>
        ) : (
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Bill No</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th className="text-end">Qty ส่ง</th>
                      <th className="text-end">ขายแล้ว</th>
                      <th className="text-end">ต้นทุน</th>
                      <th className="text-end">USD รวม</th>
                      <th className="text-end">THB รวม</th>
                      <th className="text-end">กำไร</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((item) => {
                      const isExpanded = expandedBillNos[item.bill_no] === true;
                      const isUpdatingStatus = statusUpdatingBillNo === item.bill_no;

                      const totalQty = item.order_items.reduce((s, oi) => s + oi.qty, 0);
                      const totalQtySold = item.order_items.reduce((s, oi) => s + oi.usa_qty_sold, 0);
                      // PMN-CRITICAL(/USASalesPayoutPage): bill cost column shows all item cost, while profit only counts cost of qty actually sold.
                      const totalCostAll = item.order_items.reduce((s, oi) => s + oi.unit_cost * oi.qty, 0);
                      const totalCostSold = item.order_items.reduce(
                        (s, oi) => s + oi.unit_cost * oi.usa_qty_sold,
                        0
                      );
                      const sumThb = item.order_items.reduce((s, oi) => s + oi.usa_net_thb, 0);
                      const sumUsd = item.order_items.reduce((s, oi) => s + oi.usa_net_usd, 0);
                      const hasSale = totalQtySold > 0;
                      // PMN-CRITICAL(/USASalesPayoutPage): block profit display when bill-level rate is not yet set (0). Even if sale records exist, profit is hidden until user enters bill rate.
                      const hasBillRate = item.usa_exchange_rate > 0;
                      const profit = sumThb - totalCostSold;

                      return (
                        <React.Fragment key={item.bill_no}>
                          <tr className={isExpanded ? "table-primary" : undefined}>
                            <td className="fw-semibold">#{item.bill_no}</td>
                            <td>{formatDateTime(item.created_at)}</td>
                            <td>
                              <span className={getStatusBadgeClass(item.usa_status)}>
                                {getStatusLabel(item.usa_status)}
                              </span>
                            </td>
                            <td className="text-end">{totalQty}</td>
                            <td className="text-end">
                              <span className={totalQtySold === totalQty ? "text-success fw-semibold" : ""}>
                                {totalQtySold}/{totalQty}
                              </span>
                            </td>
                            <td className="text-end">{formatCurrency(totalCostAll)}</td>
                            <td className="text-end">{formatCurrency(sumUsd, "USD")}</td>
                            <td className="text-end">{formatCurrency(sumThb)}</td>
                            <td
                              className={`text-end fw-semibold ${!hasBillRate || !hasSale ? "text-muted" : profit >= 0 ? "text-success" : "text-danger"}`}
                              title={!hasBillRate ? "ตั้งอัตราแลกของบิลก่อนจึงคำนวณกำไร" : ""}
                            >
                              {!hasBillRate ? "⚠ ตั้ง rate" : hasSale ? formatCurrency(profit) : "—"}
                            </td>
                            <td className="text-end">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() =>
                                  setExpandedBillNos((prev) => ({
                                    ...prev,
                                    [item.bill_no]: !isExpanded,
                                  }))
                                }
                              >
                                {isExpanded ? "Hide" : "Details"}
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={10} className="bg-light">
                                <div className="p-3">
                                  {/* Bill-level exchange rate — used as fallback for new sale records */}
                                  <div className="card border-0 shadow-sm rounded-3 mb-3">
                                    <div className="card-body p-3">
                                      <div className="d-flex flex-wrap align-items-end gap-2">
                                        <div>
                                          <label className="form-label small mb-1">
                                            อัตราแลกเปลี่ยนของบิลนี้ (1 USD = ? THB)
                                          </label>
                                          <input
                                            type="text"
                                            inputMode="decimal"
                                            className="form-control"
                                            style={{ width: 180 }}
                                            placeholder="เช่น 35.50"
                                            value={billRateInput[item.bill_no] ?? String(item.usa_exchange_rate || "")}
                                            onChange={(e) => {
                                              const raw = e.target.value;
                                              if (raw !== "" && !/^\d*\.?\d{0,4}$/.test(raw)) return;
                                              setBillRateInput((prev) => ({ ...prev, [item.bill_no]: raw }));
                                            }}
                                          />
                                        </div>
                                        <button
                                          className="btn btn-success btn-sm"
                                          disabled={savingBillRate === item.bill_no}
                                          onClick={() => void saveBillRate(item.bill_no)}
                                        >
                                          {savingBillRate === item.bill_no ? "กำลังบันทึก…" : "บันทึก rate"}
                                        </button>
                                        <div className="small text-muted">
                                          {item.usa_exchange_rate > 0 ? (
                                            <>ปัจจุบัน: <strong>{item.usa_exchange_rate.toFixed(4)}</strong> — รายการขายใหม่จะใช้ rate นี้ถ้าไม่กรอกในกล่อง modal</>
                                          ) : (
                                            <span className="text-warning">⚠ ยังไม่กรอก rate ของบิล — รายการขายต้องกรอก rate หรือ THB เอง</span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* In-bill item search — sort matches to top, keep others below */}
                                  <div className="mb-3" style={{ maxWidth: 420 }}>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      placeholder="🔍 ค้นหารหัส/ชื่อในบิลนี้ (เลื่อนขึ้นบน ไม่ตัดรายการอื่นออก)"
                                      value={itemQueryByBill[item.bill_no] ?? ""}
                                      onChange={(e) =>
                                        setItemQueryByBill((prev) => ({
                                          ...prev,
                                          [item.bill_no]: e.target.value,
                                        }))
                                      }
                                    />
                                  </div>

                                  {/* Per-item with sale records — sort by match before render */}
                                  {(() => {
                                    const rawQ = (itemQueryByBill[item.bill_no] ?? "").trim().toLowerCase();
                                    if (!rawQ) return item.order_items;
                                    const matched: USAOrderItem[] = [];
                                    const rest: USAOrderItem[] = [];
                                    for (const oi of item.order_items) {
                                      const hay = `${oi.product_code ?? ""} ${oi.product_name ?? ""}`.toLowerCase();
                                      if (hay.includes(rawQ)) matched.push(oi);
                                      else rest.push(oi);
                                    }
                                    return [...matched, ...rest];
                                  })().map((oi) => {
                                    const itemRecords = item.sale_records.filter(
                                      (r) => r.order_item_id === oi.id
                                    );
                                    const remaining = oi.qty - oi.usa_qty_sold;
                                    const rawQ = (itemQueryByBill[item.bill_no] ?? "").trim().toLowerCase();
                                    const hay = `${oi.product_code ?? ""} ${oi.product_name ?? ""}`.toLowerCase();
                                    const isMatch = rawQ !== "" && hay.includes(rawQ);
                                    return (
                                      <div
                                        key={oi.id}
                                        className={`card shadow-sm rounded-3 mb-3 ${
                                          isMatch
                                            ? "border-warning border-2 bg-warning-subtle text-warning-emphasis"
                                            : "border-0"
                                        }`}
                                      >
                                        <div className="card-body p-3">
                                          <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-2">
                                            <div>
                                              <div className="fw-semibold">{oi.product_code}</div>
                                              <div className="small text-muted">{oi.product_name}</div>
                                            </div>
                                            <div className="text-end small">
                                              <div>Qty <strong>{oi.qty}</strong> | ขายแล้ว <strong className={oi.usa_qty_sold === oi.qty ? "text-success" : ""}>{oi.usa_qty_sold}</strong> | เหลือ <strong className={remaining > 0 ? "text-warning" : "text-muted"}>{remaining}</strong></div>
                                              <div>Cost/Unit <strong>{formatCurrency(oi.unit_cost)}</strong></div>
                                              <div>รวมที่ได้ <strong>{formatCurrency(oi.usa_net_usd, "USD")} / {formatCurrency(oi.usa_net_thb)}</strong></div>
                                            </div>
                                          </div>

                                          {/* Sale records list */}
                                          {itemRecords.length > 0 && (
                                            <div className="table-responsive mb-2">
                                              <table className="table table-sm align-middle mb-0">
                                                <thead className="table-light">
                                                  <tr>
                                                    <th>เมื่อ</th>
                                                    <th className="text-end">Qty</th>
                                                    <th className="text-end">USD</th>
                                                    <th className="text-end">Rate</th>
                                                    <th className="text-end">THB</th>
                                                    <th>Note</th>
                                                    <th />
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {itemRecords.map((rec) => (
                                                    <tr key={rec.id}>
                                                      <td className="small">{formatDateTime(rec.sold_at)}</td>
                                                      <td className="text-end">{rec.qty}</td>
                                                      <td className="text-end">{formatCurrency(rec.usd, "USD")}</td>
                                                      <td className="text-end small text-muted">{rec.exchange_rate.toFixed(4)}</td>
                                                      <td className="text-end">{formatCurrency(rec.thb)}</td>
                                                      <td className="small text-muted">{rec.note ?? ""}</td>
                                                      <td className="text-end">
                                                        <button
                                                          className="btn btn-sm btn-outline-secondary me-1"
                                                          onClick={() => openEditRecord(item.bill_no, oi, rec)}
                                                        >
                                                          แก้
                                                        </button>
                                                        <button
                                                          className="btn btn-sm btn-outline-danger"
                                                          onClick={() => void deleteRecord(rec)}
                                                        >
                                                          ลบ
                                                        </button>
                                                      </td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            </div>
                                          )}

                                          {/* Add record button */}
                                          {remaining > 0 ? (
                                            <button
                                              className="btn btn-sm btn-primary"
                                              onClick={() => openCreateRecord(item.bill_no, oi)}
                                            >
                                              + บันทึกการขาย (เหลือ {remaining})
                                            </button>
                                          ) : (
                                            <div className="text-success small">✓ ขายหมดแล้ว</div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}

                                  {/* Status + Undo */}
                                  <div className="d-flex gap-2 justify-content-end mt-3">
                                    <button
                                      className={`btn ${item.usa_status === "COMPLETED" ? "btn-warning" : "btn-success"}`}
                                      disabled={isUpdatingStatus}
                                      onClick={() =>
                                        void handleStatusUpdate(
                                          item,
                                          item.usa_status === "COMPLETED" ? "PENDING" : "COMPLETED"
                                        )
                                      }
                                    >
                                      {isUpdatingStatus
                                        ? "กำลังอัปเดต…"
                                        : item.usa_status === "COMPLETED"
                                          ? "↩ กลับเป็น PENDING"
                                          : "✓ ทำเครื่องหมายเสร็จสิ้น"}
                                    </button>
                                  </div>

                                  {/* Danger Zone */}
                                  <div className="mt-3 border-top pt-3">
                                    <details>
                                      <summary className="text-danger fw-semibold" style={{ cursor: "pointer" }}>
                                        ⚠ Danger Zone — ย้อนกลับบิลนี้
                                      </summary>
                                      <div className="alert alert-danger mt-2 small mb-2">
                                        การย้อนกลับจะ <strong>ลบบิล + คืน stock</strong> ทั้งหมด<br />
                                        บิลที่ <strong>มีประวัติการขาย</strong> ต้องลบประวัติก่อน<br />
                                        บิลที่ <strong>เสร็จสิ้นแล้ว</strong> ห้ามย้อน
                                      </div>
                                      {item.usa_status === "COMPLETED" ? (
                                        <button className="btn btn-outline-danger" disabled>
                                          ห้าม undo (COMPLETED)
                                        </button>
                                      ) : item.sale_records.length > 0 ? (
                                        <button className="btn btn-outline-danger" disabled>
                                          ห้าม undo (มีประวัติการขาย {item.sale_records.length} รายการ)
                                        </button>
                                      ) : (
                                        <>
                                          <label className="form-label small">
                                            พิมพ์ <code>{item.bill_no}</code> เพื่อยืนยัน
                                          </label>
                                          <input
                                            type="text"
                                            className="form-control form-control-sm mb-2"
                                            placeholder={item.bill_no}
                                            value={undoConfirmInput[item.bill_no] ?? ""}
                                            onChange={(e) =>
                                              setUndoConfirmInput((prev) => ({
                                                ...prev,
                                                [item.bill_no]: e.target.value,
                                              }))
                                            }
                                          />
                                          <button
                                            type="button"
                                            className="btn btn-danger"
                                            disabled={
                                              undoingBillNo === item.bill_no ||
                                              (undoConfirmInput[item.bill_no] ?? "") !== item.bill_no
                                            }
                                            onClick={() => void handleUndo(item)}
                                          >
                                            {undoingBillNo === item.bill_no ? "กำลังย้อน…" : "↩ Undo บิลนี้"}
                                          </button>
                                        </>
                                      )}
                                    </details>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sale Record Modal */}
      {recordModal && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: "rgba(0,0,0,0.55)", zIndex: 3000 }}
          onClick={(e) => { if (e.target === e.currentTarget) closeRecordModal(); }}
        >
          <div className="card shadow-lg border-0 rounded-4" style={{ maxWidth: 520, width: "94%" }}>
            <div className="card-body p-4">
              <h5 className="mb-3 fw-bold">
                {recordModal.edit ? "แก้ไขรายการขาย" : "บันทึกการขายใหม่"}
              </h5>
              <div className="small text-muted mb-3">
                <div><strong>{recordModal.product_code}</strong></div>
                <div>{recordModal.product_name}</div>
                <div>เหลือสูงสุด: {recordModal.remaining} ชิ้น</div>
              </div>

              <div className="row g-3">
                <div className="col-6">
                  <label className="form-label small">จำนวน (qty)</label>
                  <input
                    type="number"
                    min={1}
                    max={recordModal.remaining}
                    step={1}
                    className="form-control"
                    value={recordForm.qty}
                    onChange={(e) => setRecordForm((p) => ({ ...p, qty: e.target.value }))}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small">USD ที่ได้รับ</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="form-control"
                    value={recordForm.usd}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw !== "" && !/^\d*\.?\d{0,2}$/.test(raw)) return;
                      onUsdOrRateChange(raw, recordForm.exchange_rate);
                    }}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small">อัตราแลก (1 USD = ? THB)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="form-control"
                    value={recordForm.exchange_rate}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw !== "" && !/^\d*\.?\d{0,4}$/.test(raw)) return;
                      onUsdOrRateChange(recordForm.usd, raw);
                    }}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small">THB ที่ได้รับ (override ได้)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="form-control"
                    value={recordForm.thb}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw !== "" && !/^\d*\.?\d{0,2}$/.test(raw)) return;
                      setRecordForm((p) => ({ ...p, thb: raw }));
                    }}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label small">หมายเหตุ (optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="เช่น ชื่อลูกค้า, ช่องทาง"
                    value={recordForm.note}
                    onChange={(e) => setRecordForm((p) => ({ ...p, note: e.target.value }))}
                  />
                </div>
              </div>

              <div className="d-flex gap-2 justify-content-end mt-3">
                <button className="btn btn-outline-secondary" disabled={savingRecord} onClick={closeRecordModal}>
                  ยกเลิก
                </button>
                <button className="btn btn-primary" disabled={savingRecord} onClick={() => void saveRecord()}>
                  {savingRecord ? "กำลังบันทึก…" : recordModal.edit ? "บันทึกการแก้ไข" : "บันทึก"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
